/**
 * List-page state that lives in the URL (Corbin, 2026-10-20: going into an operator and back lost
 * the "devon" search and the filters).
 *
 * Each filter is a query parameter, so browser back, a refresh and a shared link all restore the
 * list exactly. Values equal to their default are left out of the URL, and changes REPLACE the
 * history entry, so typing a search does not stack one back-step per keystroke.
 *
 * A list row opens its detail page with `listFrom(location)` as router state; the detail page's
 * own "Back" goes to `useBackTo(<list path>)`, which is that URL (search and filters included),
 * or the bare list path when the page was opened directly.
 */

import { useLocation, useSearchParams, type Location } from 'react-router-dom';

/**
 * One string query parameter with a default; the setter drops it from the URL at the default, and
 * clears `resets` (the page by default) in the SAME update, so one click never needs two URL
 * writes (a second write in the same tick would start from the stale params and undo the first).
 */
export function useUrlParam(
  key: string,
  def = '',
  resets: readonly string[] = ['page'],
): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? def;
  const set = (next: string): void => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === def || next === '') p.delete(key);
        else p.set(key, next);
        // A filter change returns to the first page of a paged list (and closes what it lists).
        for (const r of resets) if (r !== key) p.delete(r);
        return p;
      },
      { replace: true },
    );
  };
  return [value, set];
}

/** A boolean toggle as `?key=1`. */
export function useUrlFlag(
  key: string,
  resets: readonly string[] = ['page'],
): [boolean, (on: boolean) => void] {
  const [v, set] = useUrlParam(key, '', resets);
  return [v === '1', (on): void => set(on ? '1' : '')];
}

/** A whole-number parameter (a page index), default 0. */
export function useUrlNumber(
  key: string,
  resets: readonly string[] = [],
): [number, (n: number) => void] {
  const [v, set] = useUrlParam(key, '', resets);
  const n = Number.parseInt(v, 10);
  return [Number.isFinite(n) && n > 0 ? n : 0, (next): void => set(next > 0 ? String(next) : '')];
}

export interface ListFromState {
  readonly from: string;
}

/** Router state for opening a detail page from a list: remember the list's exact URL. */
export function listFrom(location: Location): ListFromState {
  return { from: `${location.pathname}${location.search}` };
}

/** Where a detail page's Back goes: the list URL it was opened from, else `fallback`. */
export function useBackTo(fallback: string): string {
  const location = useLocation();
  const state = location.state as Partial<ListFromState> | null;
  const from = state?.from;
  return typeof from === 'string' && from.startsWith(fallback) ? from : fallback;
}
